import * as React from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { Checkbox, Label, Link, MessageBar, MessageBarType, PrimaryButton, Spinner, Stack, Text, TextField } from "office-ui-fabric-react";
import { IDataProvider } from "../../dataprovider/IDataProvider";
import { Common } from "./Common";
import { GeneralHelper } from "./LoginHelper";
import { AccountType, IAuthenticatedUser, IUserAccount } from "./LoginComponent";

type CompanyFormMode = "login" | "register";

interface IFormErrors {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  legalNotice: string;
  privacyNotice: string;
}

interface IPasswordStatus {
  label: string;
  className: string;
  valid: boolean;
}

export interface ICompanyAccountFormProps {
  dataProvider: IDataProvider;
  mode: CompanyFormMode;
  setAuthStatus?: (loggedIn: boolean, user: IAuthenticatedUser) => Promise<void>;
  onAuthenticated?: (user: IAuthenticatedUser) => void;
  onNavigateToCreateAccount?: () => void;
  onNavigateToSignIn?: () => void;
}

const createEmptyUser = (): IUserAccount => ({
  firstName: "",
  lastName: "",
  email: "",
  accountType: AccountType.Custom,
  password: "",
  userId: "",
});

const emptyErrors = (): IFormErrors => ({
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
  legalNotice: "",
  privacyNotice: "",
});

const evaluatePassword = (password: string): IPasswordStatus | null => {
  if (!password) {
    return null;
  }

  const strongPassword = new RegExp("(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{8,})");
  const mediumPassword = new RegExp("((?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{6,}))|((?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9])(?=.{8,}))");

  if (strongPassword.test(password)) {
    return { label: "Strong password", className: "passwordStatusSuccess", valid: true };
  }

  if (mediumPassword.test(password)) {
    return { label: "Too short", className: "passwordStatusWarning", valid: false };
  }

  return { label: "Weak password", className: "passwordStatusDanger", valid: false };
};

export const CompanyAccountForm = (props: ICompanyAccountFormProps): React.ReactElement => {
  const isRegisterMode = props.mode === "register";
  const reCaptchaRef = React.useRef<ReCAPTCHA>(null);
  const [currentUser, setCurrentUser] = React.useState<IUserAccount>(createEmptyUser());
  const [confirmPassword, setConfirmPassword] = React.useState<string>("");
  const [errors, setErrors] = React.useState<IFormErrors>(emptyErrors());
  const [legalNoticeChecked, setLegalNoticeChecked] = React.useState<boolean>(false);
  const [privacyNoticeChecked, setPrivacyNoticeChecked] = React.useState<boolean>(false);
  const [reCaptchaToken, setReCaptchaToken] = React.useState<string>("");
  const [reCaptchaError, setReCaptchaError] = React.useState<boolean>(false);
  const [isProcessing, setIsProcessing] = React.useState<boolean>(false);
  const [isValidated, setIsValidated] = React.useState<boolean>(false);
  const [registered, setRegistered] = React.useState<boolean>(false);
  const [passwordStatus, setPasswordStatus] = React.useState<IPasswordStatus | null>(null);
  const [informationMessage, setInformationMessage] = React.useState<React.ReactNode>(null);
  const [informationType, setInformationType] = React.useState<MessageBarType>(MessageBarType.warning);

  const setInformation = React.useCallback((message: React.ReactNode, type: MessageBarType, autoDismiss = false) => {
    setInformationMessage(message);
    setInformationType(type);

    if (autoDismiss) {
      window.setTimeout(() => {
        setInformationMessage(null);
      }, 4000);
    }
  }, []);

  const validate = React.useCallback((): boolean => {
    const nextErrors = emptyErrors();
    let invalid = false;

    if (!currentUser.email || currentUser.email.trim().length === 0) {
      nextErrors.email = GeneralHelper.mandatoryValidation;
      invalid = true;
    } else if (currentUser.email.indexOf("@") === -1) {
      nextErrors.email = GeneralHelper.emailValidation;
      invalid = true;
    }

    if (!currentUser.password || currentUser.password.trim().length === 0) {
      nextErrors.password = GeneralHelper.mandatoryValidation;
      invalid = true;
    }

    if (isRegisterMode) {
      if (!currentUser.firstName || currentUser.firstName.trim().length === 0) {
        nextErrors.firstName = GeneralHelper.mandatoryValidation;
        invalid = true;
      }

      if (!currentUser.lastName || currentUser.lastName.trim().length === 0) {
        nextErrors.lastName = GeneralHelper.mandatoryValidation;
        invalid = true;
      }

      if (confirmPassword !== currentUser.password) {
        nextErrors.confirmPassword = GeneralHelper.confirmationPasswordInvalid;
        invalid = true;
      }

      if (!passwordStatus?.valid) {
        invalid = true;
      }

      if (!legalNoticeChecked) {
        nextErrors.legalNotice = "You must agree to StraumannGroup's Legal Notice";
        invalid = true;
      }

      if (!privacyNoticeChecked) {
        nextErrors.privacyNotice = "You must agree to StraumannGroup's Privacy Notice";
        invalid = true;
      }
    }

    if (!reCaptchaRef.current?.getValue()) {
      setReCaptchaError(true);
      invalid = true;
    } else {
      setReCaptchaError(false);
    }

    setErrors(nextErrors);
    setIsValidated(true);
    return !invalid;
  }, [confirmPassword, currentUser, isRegisterMode, legalNoticeChecked, passwordStatus, privacyNoticeChecked]);

  const resendActivationCode = React.useCallback(() => {
    setInformation("Sending a new confirmation email...", MessageBarType.warning);

    props.dataProvider
      .resendActivationCode(currentUser.email, Common.getCurrentServerUrl())
      .then(() => {
        setInformation("Confirmation email sent successfully.", MessageBarType.success, true);
      })
      .catch((error) => {
        const typedError = error as any;
        const errorMessage = typedError?.detail || typedError?.message || "Unable to resend confirmation email.";
        setInformation(errorMessage, MessageBarType.error);
      });
  }, [currentUser.email, props.dataProvider, setInformation]);

  const handleLogin = React.useCallback(async () => {
    if (!validate()) {
      setInformation("Please fix the validation issues.", MessageBarType.error, true);
      return;
    }

    setIsProcessing(true);
    setInformationMessage(null);

    try {
      const authenticatedUser = await props.dataProvider.loginInternalUserAccount(currentUser, reCaptchaToken);
      window.sessionStorage.setItem("authToken", JSON.stringify(authenticatedUser.bearerToken));
      window.sessionStorage.setItem("stlUser", JSON.stringify(authenticatedUser));

      if (props.setAuthStatus) {
        await props.setAuthStatus(true, authenticatedUser);
      }

      props.onAuthenticated?.(authenticatedUser);
    } catch (error) {
      window.sessionStorage.removeItem("authToken");
      window.sessionStorage.removeItem("stlUser");

      const typedError = error as any;
      let errorMessage: React.ReactNode = typedError;
      if (typedError?.detail) {
        errorMessage = typedError.detail;
      } else if (typedError?.message) {
        errorMessage = typedError.message;
      }

      if (errorMessage === "Account is not active") {
        setInformation(
          <>
            Account is not active. <Link onClick={resendActivationCode}>Resend confirmation email</Link>
          </>,
          MessageBarType.error,
        );
      } else if (errorMessage === "reCaptcha Token expired or is invalid") {
        setInformation("reCaptcha token has expired. Please try again.", MessageBarType.error);
      } else {
        setInformation(errorMessage as React.ReactNode, MessageBarType.error);
      }

      reCaptchaRef.current?.reset();
      setReCaptchaToken("");
    } finally {
      setIsProcessing(false);
    }
  }, [currentUser, props, reCaptchaToken, resendActivationCode, setInformation, validate]);

  const handleRegister = React.useCallback(async () => {
    if (!validate()) {
      setInformation("Please fix the validation issues.", MessageBarType.error, true);
      return;
    }

    setIsProcessing(true);
    setInformationMessage(null);

    try {
      await props.dataProvider.registerInternalUserAccount(currentUser, reCaptchaToken);
      setRegistered(true);
    } catch (error) {
      const typedError = error as any;
      let errorMessage: React.ReactNode = typedError;
      if (typedError?.detail) {
        errorMessage = typedError.detail;
      } else if (typedError?.status) {
        errorMessage = GeneralHelper.parseError(typedError) || "Unable to create your account.";
      } else if (typedError?.message) {
        errorMessage = typedError.message;
      }

      if (errorMessage === "reCaptcha Token expired or is invalid") {
        setInformation("reCaptcha token has expired. Please try again.", MessageBarType.error);
      } else {
        setInformation(errorMessage as React.ReactNode, MessageBarType.error);
      }

      reCaptchaRef.current?.reset();
      setReCaptchaToken("");
    } finally {
      setIsProcessing(false);
    }
  }, [currentUser, props.dataProvider, reCaptchaToken, setInformation, validate]);

  const refreshValidation = React.useCallback(() => {
    if (isValidated) {
      window.setTimeout(() => validate(), 0);
    }
  }, [isValidated, validate]);

  const onFieldChange = (field: keyof IUserAccount, value: string) => {
    setCurrentUser((prevState) => ({
      ...prevState,
      [field]: value,
    }));

    setErrors((prevState) => ({
      ...prevState,
      firstName: field === "firstName" ? "" : prevState.firstName,
      lastName: field === "lastName" ? "" : prevState.lastName,
      email: field === "email" ? "" : prevState.email,
      password: field === "password" ? "" : prevState.password,
    }));

    if (field === "password") {
      setPasswordStatus(evaluatePassword(value));
    }

    refreshValidation();
  };

  const onReCaptchaChange = (token: string | null) => {
    setReCaptchaToken(token || "");
    setReCaptchaError(false);
    refreshValidation();
  };

  if (registered) {
    return (
      <Stack tokens={{ childrenGap: 16 }}>
        <MessageBar messageBarType={MessageBarType.success} isMultiline={true}>
          You have been registered successfully.
          <br />
          Please check your email and activate your account before signing in.
        </MessageBar>
        {props.onNavigateToSignIn && <Link onClick={props.onNavigateToSignIn}>Return to sign in</Link>}
      </Stack>
    );
  }

  return (
    <Stack className="companyAccountForm" tokens={{ childrenGap: 14 }}>
      <Text variant="large" className="companyAccountFormTitle">
        {isRegisterMode ? "Create a company account" : "Sign in with your company account"}
      </Text>

      {informationMessage && (
        <MessageBar messageBarType={informationType} isMultiline={true}>
          {informationMessage}
        </MessageBar>
      )}

      {isRegisterMode && (
        <>
          <TextField
            label="First name"
            value={currentUser.firstName}
            errorMessage={errors.firstName}
            onChange={(_, value) => onFieldChange("firstName", value || "")}
          />
          <TextField
            label="Last name"
            value={currentUser.lastName}
            errorMessage={errors.lastName}
            onChange={(_, value) => onFieldChange("lastName", value || "")}
          />
        </>
      )}

      <TextField
        label="Email address"
        autoComplete="off"
        value={currentUser.email}
        errorMessage={errors.email}
        onChange={(_, value) => onFieldChange("email", value || "")}
      />
      <TextField
        label="Password"
        type="password"
        autoComplete="off"
        canRevealPassword
        value={currentUser.password}
        errorMessage={errors.password}
        onChange={(_, value) => onFieldChange("password", value || "")}
      />

      {isRegisterMode && passwordStatus && (
        <Stack tokens={{ childrenGap: 6 }}>
          <Text className={`passwordStatus ${passwordStatus.className}`}>{passwordStatus.label}</Text>
          {!passwordStatus.valid && (
            <Text className="passwordStatusHint">Password must be at least 8 characters and include lowercase, uppercase, number, and special character.</Text>
          )}
        </Stack>
      )}

      {isRegisterMode && (
        <>
          <TextField
            label="Confirm password"
            type="password"
            autoComplete="off"
            canRevealPassword
            value={confirmPassword}
            errorMessage={errors.confirmPassword}
            onChange={(_, value) => {
              setConfirmPassword(value || "");
              refreshValidation();
            }}
          />
          <Stack tokens={{ childrenGap: 8 }}>
            <Stack horizontal verticalAlign="start" tokens={{ childrenGap: 8 }}>
              <Checkbox
                checked={legalNoticeChecked}
                onChange={(_, checked) => {
                  setLegalNoticeChecked(!!checked);
                  refreshValidation();
                }}
              />
              <Text>
                I agree to StraumannGroup's{" "}
                <Link href="https://www.straumann.com/en/dental-professionals/legal-notice.html" target="_blank">
                  Legal Notice
                </Link>
              </Text>
            </Stack>
            {errors.legalNotice && <Label className="companyAccountErrorText">{errors.legalNotice}</Label>}
            <Stack horizontal verticalAlign="start" tokens={{ childrenGap: 8 }}>
              <Checkbox
                checked={privacyNoticeChecked}
                onChange={(_, checked) => {
                  setPrivacyNoticeChecked(!!checked);
                  refreshValidation();
                }}
              />
              <Text>
                I acknowledge the{" "}
                <Link href="https://www.straumann.com/group/en/discover/privacy/com/en.html" target="_blank">
                  Privacy Notice
                </Link>
              </Text>
            </Stack>
            {errors.privacyNotice && <Label className="companyAccountErrorText">{errors.privacyNotice}</Label>}
          </Stack>
        </>
      )}

      <Stack className={`companyAccountCaptcha ${reCaptchaError ? "companyAccountCaptchaError" : ""}`}>
        <ReCAPTCHA ref={reCaptchaRef} sitekey="6LfB5BQgAAAAABaHfIoaG-gU0_WFjZggiNzNTLnP" onChange={onReCaptchaChange} />
        {reCaptchaError && <Label className="companyAccountErrorText">Please confirm that you are not a robot.</Label>}
      </Stack>

      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
        <PrimaryButton className="companyAccountSubmitButton" disabled={isProcessing} onClick={isRegisterMode ? handleRegister : handleLogin}>
          {isRegisterMode ? "Create account" : "Sign in"}
        </PrimaryButton>
        {isProcessing && <Spinner />}
      </Stack>

      {!isRegisterMode && (
        <>
          <Link href={`${Common.getCurrentServerUrl()}/resetpw`}>Forgot my password</Link>
          {props.onNavigateToCreateAccount && <Link onClick={props.onNavigateToCreateAccount}>Create a new account</Link>}
        </>
      )}

      {isRegisterMode && props.onNavigateToSignIn && <Link onClick={props.onNavigateToSignIn}>Already have an account? Sign in</Link>}
    </Stack>
  );
};
